type ReactRootRenderFn = (element: Element) => unknown;

interface StartDynamicReactRootsObserverOptions {
    rootsMap: Record<string, ReactRootRenderFn>;
    selector?: string;
    rootNode?: Element;
}

const DEFAULT_SELECTOR = '[data-react-root]';

export const startDynamicReactRootsObserver = ({
    rootsMap,
    selector = DEFAULT_SELECTOR,
    rootNode,
}: StartDynamicReactRootsObserverOptions) => {
    if (typeof MutationObserver === 'undefined') return;

    const getRootNode = () => rootNode ?? document.body;

    const pendingRoots = new Set<Element>();
    let flushScheduled = false;
    const scheduleMicroTask =
        typeof queueMicrotask === 'function'
            ? queueMicrotask
            : (callback: () => void) => Promise.resolve().then(callback);

    const initDynamicReactRoot = (element: Element) => {
        const componentName = element.getAttribute('data-react-root');
        if (
            !componentName ||
            element.dataset.reactRootInitialized === 'true' ||
            element.dataset.reactRootInitializing === 'true'
        ) {
            return;
        }

        const renderFn = rootsMap?.[componentName];
        if (!renderFn) return;

        element.dataset.reactRootInitializing = 'true';

        let renderResult;
        try {
            renderResult = renderFn(element);
        } catch (error) {
            delete element.dataset.reactRootInitializing;
            console.error(`Ошибка инициализации react-root "${componentName}"`, error);
            return;
        }

        Promise.resolve(renderResult)
            .then(() => {
                element.dataset.reactRootInitialized = 'true';
            })
            .catch((error) => {
                console.error(`Ошибка инициализации react-root "${componentName}"`, error);
            })
            .finally(() => {
                delete element.dataset.reactRootInitializing;
            });
    };

    const scheduleRootInitialization = (element: Element) => {
        pendingRoots.add(element);
        if (flushScheduled) return;

        flushScheduled = true;
        scheduleMicroTask(() => {
            pendingRoots.forEach(initDynamicReactRoot);
            pendingRoots.clear();
            flushScheduled = false;
        });
    };

    const scanRoots = (currentRootNode: Element) => {
        currentRootNode.querySelectorAll(selector).forEach((element) => {
            if (element instanceof Element) {
                scheduleRootInitialization(element);
            }
        });
    };

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) return;

            mutation.addedNodes.forEach((node) => {
                if (node.nodeType !== Node.ELEMENT_NODE) return;

                const element = node as Element;
                if (element.matches(selector)) {
                    scheduleRootInitialization(element);
                }

                const nestedRoots = element.querySelectorAll(selector);
                if (nestedRoots.length === 0) return;
                nestedRoots.forEach((nestedRoot) => {
                    if (nestedRoot instanceof Element) {
                        scheduleRootInitialization(nestedRoot);
                    }
                });
            });
        });
    });

    const start = () => {
        const currentRootNode = getRootNode();
        if (!currentRootNode) return;

        scanRoots(currentRootNode);
        observer.observe(currentRootNode, { childList: true, subtree: true });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
        start();
    }

    return observer;
};
